import React from 'react';

import { NavigationAnalyticsContext } from '@atlaskit/analytics-namespaced-context/NavigationAnalyticsContext';
import { AnalyticsListener, type UIAnalyticsEvent } from '@atlaskit/analytics-next';

import { type GlobalSideNavigationProps } from '../types';
import { ElementEventListener } from '../utils/element-event-listener';

import { ATLASKIT_CHANNEL, COMPONENT_NAME } from './constants';
import {
	type PackageMetaDataType,
	type SideNavMetadataType,
	type SourceMetadataType,
} from './generated/analytics.types';

export function withSideNavigationAnalyticsContext<P extends GlobalSideNavigationProps>(
	WrappedComponent: React.ComponentType<P>,
): React.ComponentType<P> {
	return (props: P) => {
		return (
			<NavigationAnalyticsContext
				data={{ ...contextData, attributes: { screen: props.app || 'unknown' } }}
			>
				<WrappedComponent {...props} />
			</NavigationAnalyticsContext>
		);
	};
}

export const withSideNavigationAnalyticsListener = (
	children: React.ReactNode,
	cb: (isRightClick?: boolean) => void,
): React.JSX.Element => {
	const handleOnEvent = (event: UIAnalyticsEvent) => {
		// Make sure we only listen to click events and fires our event inside the callback function
		event._isUIAnalyticsEvent && event.payload?.action === 'clicked' && cb?.();
	};

	return (
		// Only listen to events from the ATLASKIT_CHANNEL to narrow down the payloads we receive
		<AnalyticsListener channel={ATLASKIT_CHANNEL} onEvent={handleOnEvent}>
			<ElementEventListener handler={cb}>{children}</ElementEventListener>
		</AnalyticsListener>
	);
};

type AnalyticsGlobalContextType = PackageMetaDataType & {
	attributes: SourceMetadataType & SideNavMetadataType & {};
};

const packageMetadata: PackageMetaDataType = {
	source: 'topNav',
	componentName: COMPONENT_NAME,
	packageName: process.env._PACKAGE_NAME_ ?? '',
	packageVersion: process.env._PACKAGE_VERSION_ ?? '',
};

const sourceMetadata: SourceMetadataType = {
	navLayer: 'global',
	navContainer: 'top',
	navVersion: '4',
};

const sideNavMetadata: SideNavMetadataType = {
	screen: 'unknown',
};

const contextData: AnalyticsGlobalContextType = {
	...packageMetadata,
	attributes: {
		...sideNavMetadata,
		...sourceMetadata,
	},
};
